import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import AuthExperienceShell from './AuthExperienceShell';
import axios from 'axios';

import { BACKEND_URL } from '../config/api';

const RegisterBasic = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: ''
  });
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [existingDriver, setExistingDriver] = useState(null);
  const [showLinkPrompt, setShowLinkPrompt] = useState(false);
  const [tourRole, setTourRole] = useState('Lead Driver');
  const navigate = useNavigate();

  const roles = [
    { value: 'driver', label: 'Driver - Looking for gigs', description: 'Browse and apply for driving positions in the entertainment industry' },
    { value: 'bus_company', label: 'Bus Company - Hiring drivers', description: 'Post jobs and manage your fleet operations' },
    { value: 'guest_tour', label: 'Tour Organizer - Need drivers', description: 'Post tour requirements and hire qualified drivers' }
  ];

  // Pre-select role from ?role= query param (landing-page segmentation buttons)
  useEffect(() => {
    const param = new URLSearchParams(window.location.search).get('role');
    if (!param) return;
    const map = {
      driver: 'driver',
      operator: 'bus_company',
      company: 'bus_company',
      bus_company: 'bus_company',
      tour: 'guest_tour',
      guest_tour: 'guest_tour',
    };
    const mapped = map[param.toLowerCase()];
    if (mapped) {
      setFormData((prev) => ({ ...prev, role: mapped }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Check for existing driver account when tour organizer is selected
  useEffect(() => {
    if (formData.role !== 'guest_tour' || !formData.email || formData.email.length < 5) {
      setExistingDriver(null);
      setShowLinkPrompt(false);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.post(`${BACKEND_URL}/api/auth/check-existing-driver`, { email: formData.email });
        if (res.data.exists) {
          setExistingDriver(res.data);
          setShowLinkPrompt(true);
        } else {
          setExistingDriver(null);
          setShowLinkPrompt(false);
        }
      } catch { setExistingDriver(null); }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.email, formData.role]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validation
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    if (!formData.role) {
      setError('Please select your role');
      setLoading(false);
      return;
    }

    if (!acceptedPrivacy) {
      setError("You must accept the Privacy Policy & Data-Sharing Acknowledgment to create an account.");
      setLoading(false);
      return;
    }

    try {
      // Handle linked tour organizer + driver registration
      if (formData.role === 'guest_tour' && showLinkPrompt && existingDriver) {
        const response = await axios.post(`${BACKEND_URL}/api/auth/register-tour-organizer-linked`, {
          email: formData.email,
          password: formData.password,
          full_name: formData.full_name,
          tour_role: tourRole,
        });
        localStorage.setItem('token', response.data.access_token);
        navigate('/dashboard');
        return;
      }

      // Create basic account first
      const response = await fetch(`${BACKEND_URL}/api/auth/register-basic`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          full_name: formData.full_name,
          role: formData.role
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Registration failed');
      }

      // Account created successfully - now redirect to login with instructions
      navigate('/login', { 
        state: { 
          email: formData.email,
          message: 'Account created successfully! Please login to complete your profile setup.',
          showProfileSetupMessage: true
        } 
      });
    } catch (err) {
      setError(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthExperienceShell
      badge="Fast Onboarding"
      title="Create Your HopSeat Account"
      description="Set your role and credentials to unlock a tailored workspace for driving gigs, fleet hiring, or tour staffing operations."
      backTo="/"
      backLabel="Back to Home"
      testId="register-experience-shell"
    >
      <div>
        <div className="mb-6 text-center">
          <Link to="/" className="inline-flex items-center space-x-2 text-orange-400 hover:text-orange-300 mb-3" data-testid="register-brand-home-link">
            <img
              src="/favicon-192x192.png?v=20260609"
              alt="HopSeat Logo"
              className="h-8 w-8 object-contain"
            />
            <span className="text-xl font-bold hopseat-brand">HopSeat</span>
          </Link>
          <p className="text-sm text-slate-300">Step 1 of 2: Create your account</p>
        </div>

        <div className="rounded-xl border border-white/10 bg-black/20 p-4 md:p-5">
          {error && (
            <div className="alert alert-error mb-4" data-testid="register-error-alert">
              <AlertCircle className="h-5 w-5 inline mr-2" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} data-testid="register-basic-form">
            {/* Google Sign-Up Button — account type comes from the "I am a..." dropdown below */}
            <div className="mb-3">
              <button
                type="button"
                onClick={() => {
                  const apiBase = window.location.origin;
                  window.location.href = `${apiBase}/api/auth/google/login?role=${formData.role || 'driver'}`;
                }}
                className="w-full flex items-center justify-center gap-2 py-2 px-4 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-sm text-white transition-all"
                data-testid="google-signup-button"
              >
                <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Sign up with Google
              </button>
            </div>

            {/* Apple Sign-Up Button — Apple HIG compliant (black bg, white logo + text) */}
            <div className="mb-4">
              <button
                type="button"
                onClick={() => {
                  const apiBase = window.location.origin;
                  window.location.href = `${apiBase}/api/auth/apple/login?role=${formData.role || 'driver'}`;
                }}
                className="w-full flex items-center justify-center gap-2 py-2 px-4 rounded-lg bg-black text-white font-medium hover:bg-neutral-900 text-sm transition-all border border-black"
                data-testid="apple-signup-button"
                aria-label="Sign up with Apple"
              >
                <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="#FFFFFF" aria-hidden="true">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                Sign up with Apple
              </button>
            </div>

            {/* RoadOps Sign-Up Button — partner OIDC integration */}
            <div className="mb-4">
              <button
                type="button"
                onClick={() => {
                  const apiBase = window.location.origin;
                  window.location.href = `${apiBase}/api/auth/roadops/login?role=${formData.role || 'driver'}`;
                }}
                className="w-full flex items-center justify-center gap-2 py-2 px-4 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-sm text-white font-medium transition-all"
                data-testid="roadops-signup-button"
                aria-label="Sign up with RoadOps"
              >
                <img src="/roadops-logo.png?v=20260609" alt="" className="w-4 h-4 flex-shrink-0 object-contain" aria-hidden="true" />
                Sign up with RoadOps
              </button>
            </div>

            <div className="relative mb-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-gray-900/80 px-3 text-gray-500">or create with email</span>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="full_name" className="form-label">Full Name</label>
              <input
                type="text"
                id="full_name"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                className="form-input"
                required
                placeholder="Enter your full name"
                data-testid="register-full-name-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email" className="form-label">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="form-input"
                required
                placeholder="Enter your email"
                data-testid="register-email-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="role" className="form-label">I am a...</label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="form-select"
                required
                data-testid="register-role-select"
              >
                <option value="">Select your role on HopSeat</option>
                {roles.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
              {formData.role && (
                <p className="mt-2 text-sm text-gray-400">
                  <span className="text-orange-500">→</span> {roles.find(r => r.value === formData.role)?.description}
                </p>
              )}
            </div>

            {/* Existing Driver Account Detection */}
            {formData.role === 'guest_tour' && showLinkPrompt && existingDriver && (
              <div className="p-4 rounded-xl border border-orange-500/30 bg-orange-500/10" data-testid="link-driver-prompt">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-orange-600/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="h-5 w-5 text-orange-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-white">Driver account found!</p>
                    <p className="text-xs text-gray-400 mt-1">
                      We found an existing driver profile for <strong className="text-orange-400">{existingDriver.driver_name}</strong>.
                      Your accounts will be linked so you can switch between Driver and Tour Organizer dashboards.
                    </p>
                    <div className="mt-3">
                      <label className="text-xs font-medium text-gray-400 mb-1.5 block">Your tour role:</label>
                      <div className="flex flex-wrap gap-1.5">
                        {['Tour Manager', 'Production Manager', 'Lead Driver'].map(r => (
                          <button
                            key={r}
                            type="button"
                            onClick={() => setTourRole(r)}
                            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                              tourRole === r
                                ? 'bg-orange-600/20 text-orange-400 border-orange-500/40'
                                : 'bg-gray-800 text-gray-500 border-gray-700 hover:border-orange-500/30'
                            }`}
                            data-testid={`link-role-${r.replace(/\s+/g, '-').toLowerCase()}`}
                          >
                            {r}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="password" className="form-label">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="form-input pr-12"
                  required
                  placeholder="Create a password"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-200"
                  data-testid="register-password-visibility-toggle"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              <p className={`mt-1 text-xs ${formData.password && formData.password.length < 6 ? 'text-red-400' : 'text-gray-400'}`} data-testid="register-password-hint">
                Use at least 6 characters. Tap the eye icon to show or hide your password.
              </p>
            </div>

            {/* Privacy Policy & Data-Sharing acknowledgment — REQUIRED */}
            <label className="flex items-start gap-3 rounded-lg border border-orange-500/30 bg-orange-500/5 p-3 cursor-pointer">
              <input
                type="checkbox"
                checked={acceptedPrivacy}
                onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-orange-500 flex-shrink-0"
                data-testid="register-privacy-checkbox"
                required
              />
              <span className="text-xs text-gray-200 leading-relaxed">
                I have read and accept the{' '}
                <a
                  href="/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-orange-400 underline hover:text-orange-300"
                >
                  HopSeat Privacy Policy &amp; Data-Sharing Acknowledgment
                </a>
                .{' '}
                {formData.role === 'driver' && (
                  <span>I knowingly authorise HopSeat to share my profile and personal information (including license, medical, and DQ-file documents) with bus companies and tour organizers when I apply for their gigs.</span>
                )}
                {(formData.role === 'bus_company' || formData.role === 'guest_tour') && (
                  <span>I confirm that, by hiring drivers through HopSeat, I am and will be in possession of sensitive and personal information and data on those drivers, and I will store, restrict access to, retain, and dispose of that information lawfully.</span>
                )}
              </span>
            </label>

            <button
              type="submit"
              disabled={loading || !acceptedPrivacy}
              className="w-full btn-primary flex items-center justify-center"
              data-testid="register-submit-button"
            >
              {loading ? (
                <>
                  <div className="loading-spinner mr-2"></div>
                  Creating Account...
                </>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-300">
              Already part of the HopSeat community?{' '}
              <Link to="/login" className="text-orange-500 hover:text-orange-400 font-semibold">
                Sign in here
              </Link>
            </p>
          </div>
        </div>
      </div>
    </AuthExperienceShell>
  );
};

export default RegisterBasic;